/**
 * Running a plan diff against `/api/v1/map`.
 *
 * The half of the sync that talks; `sync.ts` is the half that decides, and it
 * is pure so it can be tested without a server. Split for the same reason
 * `app/domain/reconcile.py` is split from `app/reconcile_apply.py`.
 *
 * ⚠ **The id table.** A create mints its id on the SERVER, but the document
 * already holds a local one and so does the undo stack; rewriting either
 * would corrupt history. So the local id is kept and translated here, for the
 * session. The next load rebuilds the document from the server and the ids
 * simply are the server's.
 */

import type { Op } from './sync'
import { wireAddress } from './sync'

export type Api = {
  post: (path: string, body?: unknown) => Promise<any>
  /** P6.4c. Sets a shelf's address — a PUT because it replaces the whole
   *  thing, and the server keeps that promise: re-binding a shelf to the cell
   *  it already occupies is a 200 no-op, so a double-tap is not a refusal. */
  put: (path: string, body: unknown) => Promise<any>
  patch: (path: string, body: unknown) => Promise<any>
  del: (path: string) => Promise<any>
}

/** What a push could not do, in the words the screen shows. */
export type Refusal = { op: Op; status: number; detail: string }

/** How far a push got. ``done`` counts the operations that LANDED. */
export type Pushed = { done: number; refusal: Refusal | null }

export class Ids {
  private readonly table = new Map<string, string>()

  learn(local: string, remote: string): void {
    this.table.set(local, remote)
  }

  /** The server's id for a document id — itself, unless it was minted here. */
  of(local: string): string {
    return this.table.get(local) ?? local
  }
}

/**
 * Issue one call per operation, in order, and stop at the first refusal.
 *
 * ⚠ Stops rather than continues, deliberately. The operations of one gesture
 * are ordered so that later ones assume earlier ones landed — a bookcase
 * attaches to a room the same pass created it — so pressing on after a
 * failure sends calls whose premise is gone. The caller reverts the document
 * to the last confirmed state and shows the refusal, which is the only
 * honest thing to do with a server that said no.
 */
export async function push(
  api: Api, ops: Op[], ids: Ids, siteId: string,
): Promise<Pushed> {
  let done = 0
  for (const op of ops) {
    try {
      await run(api, op, ids, siteId)
      done += 1
    } catch (err) {
      const refusal = asRefusal(err)
      if (!refusal) throw err
      // ⚠ HOW FAR IT GOT, not just that it stopped. A review measured the
      // previous shape: the caller left `confirmed` where it was, so every
      // later edit re-issued the whole failed batch — including the creates
      // that had succeeded — and the library grew phantom rooms.
      return { done, refusal: { op, ...refusal } }
    }
  }
  return { done, refusal: null }
}

async function run(api: Api, op: Op, ids: Ids, siteId: string): Promise<void> {
  switch (op.kind) {
    case 'floor.add': {
      const made = await api.post('/map/floors', {
        site_id: siteId, name: op.floor.name || '—',
      })
      ids.learn(op.floor.id, made.id)
      return
    }
    case 'floor.rename':
      await api.patch(`/map/floors/${ids.of(op.id)}`,
        { name: op.name || '—' })
      return
    case 'floor.remove':
      await api.del(`/map/floors/${ids.of(op.id)}`)
      return

    case 'room.add': {
      const made = await api.post('/map/places', {
        floor_id: ids.of(op.room.floorId),
        name: op.room.name,
        rect: op.room.rect,
      })
      ids.learn(op.room.id, made.id)
      return
    }
    case 'room.edit':
      // ⚠ The storey goes in the SAME request, and the server moves the
      // room's bookcases with it — a review measured a room that changed
      // floor alone leaving its case behind and bricking it.
      await api.patch(`/map/places/${ids.of(op.room.id)}`, {
        name: op.room.name,
        rect: op.room.rect,
        ...(op.movedStorey ? { floor_id: ids.of(op.room.floorId) } : {}),
      })
      return
    case 'room.remove':
      await api.del(`/map/places/${ids.of(op.id)}`)
      return

    case 'case.add': {
      const first = op.bookcase.sections[0]
      const made = await api.post('/map/bookcases', {
        floor_id: ids.of(op.bookcase.floorId),
        place_id: op.bookcase.roomId ? ids.of(op.bookcase.roomId) : null,
        name: op.bookcase.name,
        front: op.bookcase.front,
        rect: op.bookcase.rect,
        columns: first ? first.columnLevels.length : 1,
        levels: first ? first.defaultLevels : 5,
        depth: first ? first.defaultDepth : 1,
      })
      ids.learn(op.bookcase.id, made.id)
      // ⚠ AND its first section. The previous comment here said the id was
      // "learned on the next load, and nothing addresses it before then" —
      // false, and measured: the elevation addresses it in the very next
      // gesture, so drawing a bookcase and pressing `+ column` answered
      // `404 no such section` for every bookcase drawn in a session.
      if (made.section?.id && op.bookcase.sections[0]) {
        ids.learn(op.bookcase.sections[0].id, made.section.id)
      }
      return
    }
    case 'case.edit':
      await api.patch(`/map/bookcases/${ids.of(op.bookcase.id)}`, {
        name: op.bookcase.name,
        front: op.bookcase.front,
        rect: op.bookcase.rect,
        // `place_id: null` means "unchanged" on the wire, so letting go is
        // its own flag — a JSON null cannot mean both.
        ...(op.roomChanged
          ? op.bookcase.roomId
            ? { place_id: ids.of(op.bookcase.roomId) }
            : { detach: true }
          : {}),
      })
      return
    case 'case.remove':
      // Emptying the slots is a separate, explicit call, and it reports what
      // it cost: an occupied shelf is DETACHED, never destroyed.
      await api.del(`/map/bookcases/${ids.of(op.id)}/slots`)
      await api.del(`/map/bookcases/${ids.of(op.id)}`)
      return

    case 'section.add': {
      // ⚠ One instruction: the id it stands on, or `bottom` for the floor.
      // `top` is `above_id` of whatever is topmost, so this never sends it —
      // and a section going back into the MIDDLE of a stack has no other way
      // to be said at all.
      const made = await api.post('/map/sections', {
        bookcase_id: ids.of(op.caseId),
        ...(op.aboveId ? { above_id: ids.of(op.aboveId) } : { where: 'bottom' }),
      })
      ids.learn(op.section.id, made.section.id)
      return
    }
    case 'section.columns':
      await api.patch(`/map/sections/${ids.of(op.section.id)}`,
        { columns: op.columns })
      return
    case 'section.levels':
      // 1-based on the wire; the document counts columns from 0.
      await api.patch(`/map/sections/${ids.of(op.section.id)}`,
        { column: op.col + 1, levels: op.levels })
      return
    case 'section.gaps':
      // 1-based on the wire; the document counts from 0. One instruction with
      // its sign — `gap: false` is what brings a cell back, and the server
      // mints a fresh empty shelf at the section's current default depth.
      await api.patch(`/map/sections/${ids.of(op.section.id)}/gaps`, {
        gap: op.gap,
        cells: op.cells.map((c) => ({ column: c.col + 1, level: c.level + 1 })),
      })
      return
    case 'section.defaults':
      await api.patch(`/map/sections/${ids.of(op.section.id)}`, {
        default_levels: op.section.defaultLevels,
        default_depth: op.section.defaultDepth,
      })
      return
    case 'section.remove':
      await api.del(`/map/sections/${ids.of(op.id)}/slots`)
      await api.del(`/map/sections/${ids.of(op.id)}`)
      return

    case 'shelf.depth': {
      const at = wireAddress(op.section, {
        col: op.col, level: op.level, depth: op.depth, photos: 0, books: 0,
      })
      await api.patch(
        `/map/sections/${ids.of(at.section_id)}/shelves/${at.col}/${at.level}`,
        { depth_count: op.depth })
      return
    }
  }
}

function asRefusal(err: unknown): { status: number; detail: string } | null {
  // ⚠ `message`, not just `detail`. `ApiError` (api/client.ts) copies the
  // server's `detail` into `message`; reading only `detail` made every
  // refusal render an EMPTY `role="alert"` — measured, with the server
  // saying "a bookcase holds at most 400 shelves; this would have 408"
  // and the owner seeing nothing but 43px of "not saved".
  const e = err as { status?: number; detail?: string; message?: string }
  if (typeof e?.status !== 'number') return null
  return { status: e.status, detail: e.detail || e.message || '' }
}
